import type { MouseEvent } from "react";
import clsx from "clsx";
import type { AiResolutionSuggestion, SupportTicket } from "@/lib/support";
import { STATUS_LABEL, TODO_LABEL, URGENCY_LABEL } from "@/pages/support/constants";
import { IncomingReplyDot, ReplyMailIcon } from "@/pages/support/SupportBadges";
import {
  fmtDate,
  fmtRelative,
  normalizeTodoState,
  ticketHasOutboundReply,
  ticketHasUnreadIncomingReply,
} from "@/pages/support/utils";

export function AdminTicketList({
  items,
  selectedId,
  aiSuggestions,
  onSelect,
  onTicketContextMenu,
}: {
  items: SupportTicket[];
  selectedId: number | null;
  aiSuggestions: AiResolutionSuggestion[];
  onSelect: (t: SupportTicket) => void;
  onTicketContextMenu?: (ticket: SupportTicket, event: MouseEvent) => void;
}) {
  if (!items.length) {
    return (
      <p className="py-8 text-center text-sm text-label-tertiary">
        Keine Tickets in dieser Ansicht.
      </p>
    );
  }

  return (
    <div className="space-y-1">
      {items.map((ticket) => {
        const todoState = normalizeTodoState(ticket.todo_state);
        const urgency = String(ticket.urgency || "normal").toLowerCase();
        const status = String(ticket.status || "open").toLowerCase();
        const owner = String(ticket.todo_owner_login || "").trim();
        const reporter = String(ticket.reporter_display || ticket.reporter_login || "").trim();
        const updated = ticket.updated_at || ticket.created_at;
        const sugg = aiSuggestions.find((s) => Number(s.ticket_id) === Number(ticket.id));
        const conf = sugg ? Math.round((Number(sugg.confidence) || 0) * 100) : 0;

        const unread = Boolean(ticket.is_unread);
        const incomingReply = ticketHasUnreadIncomingReply(ticket);
        const hasReplyMail = ticketHasOutboundReply(ticket);
        const hasNote = Boolean(String(ticket.my_note || "").trim());
        const tags = Array.isArray(ticket.tags) ? ticket.tags : [];

        return (
          <button
            key={ticket.id}
            type="button"
            onClick={() => onSelect(ticket)}
            onContextMenu={(e) => {
              if (!onTicketContextMenu) return;
              e.preventDefault();
              e.stopPropagation();
              onTicketContextMenu(ticket, e);
            }}
            className={clsx(
              "support-list-item w-full rounded-ios border px-3 py-2.5 text-left transition",
              selectedId === ticket.id
                ? "border-accent bg-accent-soft/30 ring-1 ring-accent/40"
                : "border-separator/60 bg-card hover:bg-fill-tertiary",
              incomingReply && selectedId !== ticket.id && "border-l-2 border-l-status-red",
              unread && !incomingReply && selectedId !== ticket.id && "border-l-2 border-l-accent",
            )}
          >
            <div className="flex items-center gap-2 text-sm">
              {incomingReply ? (
                <IncomingReplyDot />
              ) : unread ? (
                <span
                  className="h-2 w-2 shrink-0 rounded-full bg-accent"
                  title="Ungelesen"
                  aria-label="Ungelesen"
                />
              ) : (
                <span
                  className={clsx(
                    "h-2 w-2 shrink-0 rounded-full",
                    urgency === "critical" && "bg-status-red",
                    urgency === "high" && "bg-status-orange",
                    urgency === "low" && "bg-status-blue",
                    urgency === "normal" && "bg-label-tertiary",
                  )}
                  title={URGENCY_LABEL[urgency] || urgency}
                />
              )}
              <span className="font-mono text-label-secondary">#{ticket.id}</span>
              <span
                className={clsx(
                  "min-w-0 flex-1 truncate text-label",
                  unread ? "font-semibold" : "font-medium",
                )}
              >
                {ticket.title || "—"}
              </span>
              {hasReplyMail ? <ReplyMailIcon /> : null}
              {hasNote ? <span title="Private Notiz">📝</span> : null}
            </div>
            <div className="mt-1 flex flex-wrap gap-x-1 text-xs text-label-secondary">
              <span>{reporter || "Unbekannt"}</span>
              <span>·</span>
              <span>{ticket.program || "—"}</span>
              <span>·</span>
              <span title={fmtDate(updated)}>{fmtRelative(updated)}</span>
              {owner ? (
                <>
                  <span>·</span>
                  <span title="Bearbeiter">👤 {owner}</span>
                </>
              ) : null}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1">
              <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[10px] font-semibold uppercase">
                {STATUS_LABEL[status] || status}
              </span>
              <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[10px] font-semibold">
                {TODO_LABEL[todoState] || todoState}
              </span>
              {tags.slice(0, 3).map((tag) => (
                <span
                  key={tag.id}
                  className="max-w-[6rem] truncate rounded-full bg-accent-soft/40 px-2 py-0.5 text-[10px] font-semibold text-label"
                  title={tag.name}
                >
                  {tag.name}
                </span>
              ))}
              {sugg ? (
                <span className="rounded-full bg-status-green/15 px-2 py-0.5 text-[10px] font-semibold text-status-green">
                  erledigt? {conf}%
                </span>
              ) : null}
            </div>
          </button>
        );
      })}
    </div>
  );
}
